import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Lock, X } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import RichText from "@/components/RichText";

/**
 * Vertrauens-Box — dezenter Text-Link + helles Modal.
 *
 * Ersetzt die frühere dunkle Sektion. Der Trigger sitzt weiterhin
 * direkt unter „Die Idee" auf der Startseite; beim Klick öffnet sich
 * ein editorial-heller Dialog mit denselben Inhalten (CMS-Key
 * `home.trust_box`, unverändert kompatibel — nur `trigger_text` ist neu).
 *
 * A11y:
 *  - ESC schließt · Klick auf Overlay schließt · Focus-Trap
 *  - `role="dialog"`, `aria-modal`, `aria-labelledby`
 *  - `prefers-reduced-motion` respektiert (kein Scale/Fade)
 *  - `body { overflow: hidden }` solange offen
 */

export type TrustBoxContent = {
    enabled?: boolean;
    icon_choice?: "shield" | "lock" | "none";
    /** Legacy — nicht mehr im Rendering verwendet, aber für Rückwärts-
     *  Kompatibilität in DB & Admin sichtbar (siehe Admin-„Legacy"-Sektion). */
    trigger_text?: string;
    /** Kurze Headline in der Trigger-Karte. Fällt bei leerem Wert auf
     *  `headline` zurück, damit ältere Setups sofort etwas anzeigen. */
    card_headline?: string;
    /** 2–3-zeiliger Teaser in der Trigger-Karte. */
    card_teaser?: string;
    /** Beschriftung des unterstrichenen CTA-Links in der Trigger-Karte
     *  (inkl. optionalem Pfeil-Zeichen — die Komponente rendert 1:1). */
    card_cta?: string;
    /** Visuelle Variante des Triggers.
     *  - `card` (Default): kompakte Editorial-Karte mit feinem Rahmen und
     *    Papier-Hintergrund.
     *  - `editorial`: Zwischenblock ohne Card-Umrahmung, nur feine
     *    Trennlinien oben/unten — wirkt wie eine typografische Pause. */
    card_style?: "card" | "editorial";
    headline?: string;
    subheadline?: string;
    body_html?: string;
    claim?: string;
    claim_subtitle?: string;
};

export const TRUST_BOX_DEFAULTS: Required<TrustBoxContent> = {
    enabled: true,
    icon_choice: "shield",
    trigger_text: "Warum sehen Sie hier keine echten Kundenobjekte?",
    card_headline: "Ihr Vertrauen ist unsere wertvollste Referenz",
    card_teaser:
        "Aus Respekt vor der Privatsphäre unserer Kunden verzichten wir bewusst auf die Veröffentlichung echter Kundenobjekte. Stattdessen zeigen wir teilweise KI-generierte Visualisierungen – transparent, nachvollziehbar und mit größtem Respekt für die Privatsphäre unserer Kunden.",
    card_cta: "Unser Versprechen an Sie →",
    card_style: "card",
    headline: "Ihr Vertrauen ist unsere wertvollste Referenz",
    subheadline: "Diskretion und Privatsphäre stehen für uns an erster Stelle.",
    body_html:
        "<p>Wer uns beauftragt, schenkt uns sein Vertrauen und öffnet uns die Türen zu seinem Zuhause. Deshalb veröffentlichen wir grundsätzlich keine Innenaufnahmen, 360°-Rundgänge oder Videos realer Kundenobjekte ohne ausdrückliche Zustimmung.</p>" +
        "<p>Damit Sie sich dennoch ein realistisches Bild unserer Leistungen machen können, verwenden wir auf dieser Website teilweise mit Unterstützung künstlicher Intelligenz erstellte Bilder und Videos. Diese sind selbstverständlich entsprechend gekennzeichnet und dienen ausschließlich der Veranschaulichung unserer Leistungen.</p>" +
        "<p>Reale Kundenprojekte veröffentlichen wir ausschließlich dann, wenn unsere Kunden dies ausdrücklich wünschen.</p>",
    claim: "Denn nicht jede Erinnerung gehört ins Internet.",
    claim_subtitle:
        "Genau deshalb schützen wir die Privatsphäre unserer Kunden konsequent.",
};

/** Zentrales Icon-Mapping — größe steuerbar. */
export function trustIcon(choice: string | undefined, size: number) {
    if (choice === "lock") return <Lock size={size} strokeWidth={1.5} aria-hidden="true" />;
    if (choice === "none") return null;
    return <ShieldCheck size={size} strokeWidth={1.5} aria-hidden="true" />;
}

type Props = {
    /** Nur für Admin-Preview: erlaubt Bypass des globalen CMS-Stores. */
    override?: TrustBoxContent;
    /** Nur für Admin-Preview: Modal beim Mount direkt geöffnet zeigen. */
    forceOpen?: boolean;
    /** Nur für Admin-Preview: rendert Modal inline statt via Portal (damit
     *  es innerhalb des Preview-Blocks bleibt). */
    inlineModal?: boolean;
};

export default function TrustBoxSection({ override, forceOpen, inlineModal }: Props = {}) {
    const { getObject } = useSiteContent();
    const cms = override ?? getObject<TrustBoxContent>("home.trust_box", {});
    const c: Required<TrustBoxContent> = { ...TRUST_BOX_DEFAULTS, ...cms };
    const [open, setOpen] = useState<boolean>(!!forceOpen);

    // Sobald Admin-Preview forceOpen umschaltet, State nachziehen.
    useEffect(() => { setOpen(!!forceOpen); }, [forceOpen]);

    const close = useCallback(() => setOpen(false), []);

    if (c.enabled === false) return null;

    const smallIcon = trustIcon(c.icon_choice, 14);
    // Fallback-Kette für die Trigger-Headline:
    // 1) card_headline (neu), 2) trigger_text (Legacy), 3) headline (Modal-Kopie).
    const cardTitle =
        (c.card_headline && c.card_headline.trim()) ||
        (c.trigger_text && c.trigger_text.trim()) ||
        c.headline;

    const isEditorial = c.card_style === "editorial";

    // Gemeinsamer Content beider Varianten. Textausrichtung wird per
    // `align`-Prop je nach Style variiert (card = left, editorial = center).
    const innerContent = (align: "left" | "center") => (
        <>
            <div
                className={`flex items-center gap-2 ${align === "center" ? "justify-center" : ""}`}
                style={{ color: "#A78150", marginBottom: 10 }}
            >
                {smallIcon && (
                    <span aria-hidden="true" className="inline-flex items-center">
                        {smallIcon}
                    </span>
                )}
                <span
                    className="font-sans"
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.2em",
                        textTransform: "uppercase",
                    }}
                    data-testid="trust-card-meta"
                >
                    Diskretion · Vertrauen · KI-Transparenz
                </span>
            </div>

            <h3
                className="font-display font-semibold"
                style={{
                    color: "#1D1B18",
                    fontSize: "clamp(17px, 1.7vw, 19px)",
                    lineHeight: 1.3,
                    letterSpacing: "-0.005em",
                    marginBottom: 10,
                    textAlign: align,
                }}
                data-testid="trust-card-headline"
            >
                {cardTitle}
            </h3>

            {c.card_teaser && (
                <p
                    className="font-sans"
                    style={{
                        color: "#4A453F",
                        fontSize: 13,
                        lineHeight: 1.6,
                        marginBottom: 14,
                        textAlign: align,
                        ...(align === "center"
                            ? { maxWidth: 560, marginLeft: "auto", marginRight: "auto" }
                            : {}),
                    }}
                    data-testid="trust-card-teaser"
                >
                    {c.card_teaser}
                </p>
            )}

            <div style={{ textAlign: align }}>
                {(() => {
                    const raw = (c.card_cta && c.card_cta.trim()) || TRUST_BOX_DEFAULTS.card_cta;
                    // Wenn der Admin einen Pfeil am Ende gesetzt hat,
                    // separieren wir ihn für die Hover-Animation.
                    const arrowMatch = raw.match(/^(.*?)([\u2190-\u21FF\u27F0-\u27FF→←↑↓↔↗↘↙↖⟶⟵])\s*$/);
                    const label = arrowMatch ? arrowMatch[1].trimEnd() : raw;
                    const arrow = arrowMatch ? arrowMatch[2] : null;
                    return (
                        <span
                            className="inline-flex items-center gap-1 font-sans"
                            style={{
                                color: "#A78150",
                                fontSize: 13,
                                fontWeight: 500,
                                textDecoration: "underline",
                                textDecorationColor: "#A78150",
                                textUnderlineOffset: 3,
                                textDecorationThickness: 1,
                            }}
                            data-testid="trust-card-cta"
                        >
                            {label}
                            {arrow && (
                                <span
                                    className="trust-card-arrow"
                                    aria-hidden="true"
                                    style={{
                                        display: "inline-block",
                                        transition:
                                            "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
                                    }}
                                >
                                    {arrow}
                                </span>
                            )}
                        </span>
                    );
                })()}
            </div>
        </>
    );

    return (
        <section
            id="vertrauen"
            data-testid="section-trust-box"
            className="py-4 md:py-6"
            data-trust-style={c.card_style}
        >
            <div className="px-5">
                {isEditorial ? (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        data-testid="trust-box-trigger"
                        className="trust-editorial mx-auto block w-full text-center"
                        style={{
                            maxWidth: 720,
                            background: "transparent",
                            border: "none",
                            borderTop: "1px solid #D8CDBF",
                            borderBottom: "1px solid #D8CDBF",
                            padding: "28px 20px",
                            transition: "background-color 220ms ease",
                        }}
                    >
                        {innerContent("center")}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        data-testid="trust-box-trigger"
                        className="trust-card group mx-auto block w-full text-left"
                        style={{
                            maxWidth: 720,
                            background: "#FFFDF9",
                            border: "1px solid #D8CDBF",
                            borderRadius: 12,
                            padding: "24px 24px",
                            transition:
                                "background-color 220ms ease, transform 260ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease, border-color 220ms ease",
                        }}
                    >
                        {innerContent("left")}
                    </button>
                )}
            </div>
            <style>{`
                @media (min-width: 768px) {
                    .trust-card { padding: 32px 40px !important; }
                    .trust-editorial { padding: 36px 24px !important; }
                }
                .trust-card:hover {
                    background-color: #FBF6EB !important;
                    border-color: #C9BB9F !important;
                    transform: translateY(-1px);
                    box-shadow: 0 10px 24px -22px rgba(20, 12, 4, 0.30);
                    cursor: pointer;
                }
                .trust-card:hover .trust-card-arrow { transform: translateX(2px); }
                .trust-card:focus-visible {
                    outline: 2px solid #A78150;
                    outline-offset: 4px;
                    border-radius: 12px;
                }
                .trust-editorial:hover { cursor: pointer; }
                .trust-editorial:hover .trust-card-arrow { transform: translateX(2px); }
                .trust-editorial:focus-visible {
                    outline: 2px solid #A78150;
                    outline-offset: 4px;
                }
            `}</style>

            {open && (
                <TrustModal
                    content={c}
                    onClose={close}
                    inline={!!inlineModal}
                />
            )}
        </section>
    );
}

function TrustModal({
    content,
    onClose,
    inline,
}: {
    content: Required<TrustBoxContent>;
    onClose: () => void;
    inline: boolean;
}) {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const closeBtnRef = useRef<HTMLButtonElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [entered, setEntered] = useState(false);

    // Enter-Transition
    useEffect(() => {
        const t = window.setTimeout(() => setEntered(true), 16);
        return () => window.clearTimeout(t);
    }, []);

    // Body-Scroll sperren solange offen (nur bei Portal-Rendering — inline
    // im Admin-Preview soll die Seite scrollbar bleiben).
    useEffect(() => {
        if (inline) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [inline]);

    // ESC schließt
    useEffect(() => {
        if (inline) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, inline]);

    // Fokus initial auf X-Button; simpler Focus-Trap via Tab-Wrap.
    useEffect(() => {
        if (inline) return;
        closeBtnRef.current?.focus();
        const trap = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !dialogRef.current) return;
            const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener("keydown", trap);
        return () => window.removeEventListener("keydown", trap);
    }, [inline]);

    const bigIcon = trustIcon(content.icon_choice, 40);

    const modal = (
        <div
            ref={overlayRef}
            className="trust-overlay"
            data-testid="trust-box-modal-overlay"
            onMouseDown={(e) => {
                // Nur schließen wenn der Klick DIREKT aufs Overlay ging,
                // nicht wenn er im Modal begonnen und dort geendet ist.
                if (e.target === overlayRef.current) onClose();
            }}
            style={{
                position: inline ? "relative" : "fixed",
                inset: inline ? undefined : 0,
                display: "flex",
                alignItems: inline ? "flex-start" : "center",
                justifyContent: "center",
                padding: inline ? 24 : "24px 16px",
                background: inline ? "transparent" : "rgba(23, 22, 19, 0.55)",
                backdropFilter: inline ? undefined : "blur(4px)",
                WebkitBackdropFilter: inline ? undefined : "blur(4px)",
                zIndex: inline ? undefined : 1000,
                opacity: entered ? 1 : 0,
                transition: "opacity 200ms ease",
            }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal={inline ? undefined : "true"}
                aria-labelledby="trust-modal-headline"
                data-testid="trust-box-modal"
                style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 640,
                    maxHeight: inline ? "none" : "85vh",
                    overflowY: inline ? "visible" : "auto",
                    background: "#FFFDF9",
                    border: "1px solid #A78150",
                    borderRadius: 16,
                    padding: "clamp(28px, 5vw, 48px)",
                    boxShadow:
                        "0 30px 80px -20px rgba(20, 12, 4, 0.35), 0 12px 30px -12px rgba(215, 170, 100, 0.18)",
                    transform: entered ? "scale(1)" : "scale(0.96)",
                    transition:
                        "transform 200ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease",
                }}
            >
                {/* Subtile Noise-Textur */}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        opacity: 0.04,
                        borderRadius: 16,
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4  0 0 0 0 0.32  0 0 0 0 0.2  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    }}
                />

                {/* X-Close */}
                <button
                    ref={closeBtnRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Dialog schließen"
                    data-testid="trust-box-modal-close"
                    className="trust-close"
                    style={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "transparent",
                        color: "#1D1B18",
                        border: "none",
                        cursor: "pointer",
                        transition: "color 180ms ease, background 180ms ease",
                    }}
                >
                    <X size={20} strokeWidth={1.6} />
                </button>

                <div className="relative text-center">
                    {bigIcon && (
                        <div
                            aria-hidden="true"
                            className="inline-flex items-center justify-center"
                            style={{
                                color: "#A78150",
                                width: 56,
                                height: 56,
                                borderRadius: 12,
                                background: "rgba(167, 129, 80, 0.08)",
                                border: "1px solid rgba(167, 129, 80, 0.28)",
                                margin: "0 auto",
                            }}
                        >
                            {bigIcon}
                        </div>
                    )}

                    <h2
                        id="trust-modal-headline"
                        className="font-display font-semibold"
                        style={{
                            marginTop: bigIcon ? 20 : 0,
                            color: "#1D1B18",
                            fontSize: "clamp(22px, 2.4vw, 26px)",
                            lineHeight: 1.25,
                            letterSpacing: "-0.005em",
                        }}
                        data-testid="trust-modal-headline"
                    >
                        {content.headline}
                    </h2>

                    {content.subheadline && (
                        <p
                            className="font-sans"
                            style={{
                                marginTop: 12,
                                color: "#A78150",
                                fontSize: 13,
                                fontWeight: 600,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                            }}
                        >
                            {content.subheadline}
                        </p>
                    )}
                </div>

                <div
                    className="trust-modal-body font-sans"
                    style={{
                        marginTop: 28,
                        color: "#1D1B18",
                        fontSize: 15,
                        lineHeight: 1.75,
                        maxWidth: 520,
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}
                    data-testid="trust-modal-body"
                >
                    <RichText html={content.body_html} testId="trust-modal-body-content" />
                </div>

                <div
                    aria-hidden="true"
                    style={{
                        margin: "36px auto 24px",
                        width: "40%",
                        height: 1,
                        background:
                            "linear-gradient(90deg, transparent 0%, #A78150 50%, transparent 100%)",
                        opacity: 0.7,
                    }}
                />

                <p
                    className="font-display font-semibold text-center"
                    style={{
                        fontSize: "clamp(16px, 1.7vw, 18px)",
                        lineHeight: 1.35,
                        backgroundImage:
                            "linear-gradient(135deg, #B58F5B 0%, #D4A574 50%, #A78150 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        WebkitTextFillColor: "transparent",
                        maxWidth: 480,
                        margin: "0 auto",
                    }}
                    data-testid="trust-modal-claim"
                >
                    {content.claim}
                </p>

                {content.claim_subtitle && (
                    <p
                        className="font-sans italic text-center"
                        style={{
                            marginTop: 14,
                            color: "#716B63",
                            fontSize: 13,
                            lineHeight: 1.55,
                            maxWidth: 460,
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                        data-testid="trust-modal-claim-subtitle"
                    >
                        {content.claim_subtitle}
                    </p>
                )}

                <style>{`
                    .trust-close:hover { color: #A78150; background: rgba(167,129,80,0.08); }
                    .trust-modal-body p { margin: 0; }
                    .trust-modal-body p + p { margin-top: 18px; }
                `}</style>
            </div>
        </div>
    );

    if (inline || typeof document === "undefined") return modal;
    return createPortal(modal, document.body);
}
